import React, { useState, useEffect, useRef, useCallback } from "react";
import { List, X } from "lucide-react";

export default function DocsTocPopover({ headings }) {
  const [open, setOpen] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const containerRef = useRef(null);

  // IntersectionObserver to track active heading
  useEffect(() => {
    if (!headings || headings.length === 0) return;

    const observers = [];
    const visibleHeadings = new Map();

    const updateActive = () => {
      // Find the first visible heading (topmost)
      let topId = null;
      let topY = Infinity;
      for (const [id, y] of visibleHeadings.entries()) {
        if (y < topY) {
          topY = y;
          topId = id;
        }
      }
      if (topId) setActiveId(topId);
    };

    headings.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (!el) return;
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            visibleHeadings.set(id, entry.boundingClientRect.top);
          } else {
            visibleHeadings.delete(id);
          }
          updateActive();
        },
        { rootMargin: "-80px 0px -60% 0px", threshold: 0 }
      );
      observer.observe(el);
      observers.push(observer);
    });

    return () => observers.forEach((o) => o.disconnect());
  }, [headings]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function handleClick(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    function handleKey(e) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [open]);

  const handleHeadingClick = useCallback(
    (id) => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        setActiveId(id);
      }
      setOpen(false);
    },
    []
  );

  if (!headings || headings.length === 0) return null;

  return (
    <div
      ref={containerRef}
      className="fixed top-16 right-4 z-30"
    >
      {/* Toggle button */}
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 rounded-lg px-3 py-2 text-white shadow-lg transition-all"
        style={{
          background: open ? "#4B7BF5" : "#1E2D5A",
          border: "1px solid rgba(75,123,245,0.3)",
          fontSize: 12,
        }}
      >
        {open ? <X size={14} /> : <List size={14} />}
        <span className="font-['DM_Sans'] hidden sm:inline">On this page</span>
      </button>

      {/* Dropdown */}
      {open && (
        <div
          className="absolute right-0 mt-2 rounded-xl shadow-2xl overflow-hidden"
          style={{
            width: 240,
            background: "#0F1B3D",
            border: "1px solid rgba(75,123,245,0.2)",
          }}
        >
          <div
            className="px-3 py-2 border-b font-['DM_Sans'] text-white/40 font-semibold uppercase tracking-widest"
            style={{ fontSize: 10, borderColor: "rgba(255,255,255,0.08)" }}
          >
            On this page
          </div>
          <div className="py-1.5 max-h-72 overflow-y-auto">
            {headings.map(({ id, text, level }) => (
              <button
                key={id}
                onClick={() => handleHeadingClick(id)}
                className="w-full text-left px-3 py-1.5 transition-colors hover:bg-white/5"
                style={{
                  paddingLeft: level === 3 ? 24 : 12,
                  color: activeId === id ? "#4B7BF5" : "rgba(255,255,255,0.65)",
                  fontSize: 13,
                  fontFamily: "'DM Sans', sans-serif",
                }}
              >
                {text}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
